/**
 * Sales Update Screen (2.2.9)
 * View and update existing sales vouchers — narration, reference, cancel
 */
import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Save, Search, CheckCircle, AlertCircle, Edit2, X } from 'lucide-react';
import ERPAdvancedGrid from '../../components/erp/ERPAdvancedGrid';
import { erpToast } from '../../components/erp/ERPToast';
import { API_BASE as API_URL, getToken } from '../../lib/api';
import '../../styles/erp-theme.css';

const hdrs = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${getToken() || ''}` });

const STATUS_OPTIONS = ['', 'Posted', 'Unposted', 'Cancelled'];

export default function SalesUpdateScreen({ theme }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [partySearch, setPartySearch] = useState('');
  const [editing, setEditing] = useState({}); // { [docId]: { CommNarration, VoucherRef } }
  const [saving, setSaving] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortField, setSortField] = useState('DocDate');
  const [sortDir, setSortDir] = useState('desc');
  const [filters, setFilters] = useState({});

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/vouchers/sales-voucher?limit=500`;
      if (fromDate) url += `&fromDate=${fromDate}`;
      if (toDate) url += `&toDate=${toDate}`;
      const r = await fetch(url, { headers: hdrs() });
      const j = await r.json();
      if (j.success) { setRecords(j.data || []); setEditing({}); setPage(1); }
      else erpToast.error(j.error || 'Load failed');
    } catch (e) { erpToast.error(e.message); }
    setLoading(false);
  }, [fromDate, toDate]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  const setField = (docId, field, value) =>
    setEditing(prev => ({ ...prev, [docId]: { ...(prev[docId] || {}), [field]: value } }));

  const hasEdits = (docId) => !!editing[docId] && Object.keys(editing[docId]).length > 0;

  const saveRow = async (docId) => {
    const upd = editing[docId];
    if (!upd) return;
    setSaving(docId);
    try {
      const r = await fetch(`${API_URL}/api/vouchers/sales-voucher/${docId}`, {
        method: 'PUT', headers: hdrs(),
        body: JSON.stringify(upd),
      });
      const j = await r.json();
      if (j.success) {
        erpToast.success(j.message || 'Sales record updated');
        setEditing(prev => { const n = { ...prev }; delete n[docId]; return n; });
        loadRecords();
      } else erpToast.error(j.error || 'Update failed');
    } catch (e) { erpToast.error(e.message); }
    setSaving(null);
  };

  const COLUMNS = [
    { field: 'DocId', header: 'Voucher No' },
    { field: 'DocDateFormatted', header: 'Date' },
    { field: 'AccountName', header: 'Customer Account' },
    { field: 'DrAmount', header: 'Amount' },
    { field: 'CommNarration', header: 'Narration' },
    { field: 'VoucherRef', header: 'Voucher Ref' },
    { field: '_actions', header: 'Update' },
  ];

  // Filter & sort
  const displayRecords = (() => {
    let items = [...records];
    if (partySearch) {
      const s = partySearch.toLowerCase();
      items = items.filter(i => String(i.AccountName || '').toLowerCase().includes(s) || String(i.ACId || '').toLowerCase().includes(s));
    }
    Object.entries(filters).forEach(([key, val]) => {
      if (val) { const s = val.toLowerCase(); items = items.filter(i => String(i[key] || '').toLowerCase().includes(s)); }
    });
    if (sortField) items.sort((a, b) => { const av = String(a[sortField] || ''), bv = String(b[sortField] || ''); return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av); });
    return items;
  })();

  const pageData = displayRecords.slice((page - 1) * pageSize, page * pageSize);
  const editedCount = Object.keys(editing).length;

  const headerActions = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 10, color: 'var(--erp-text-muted)' }}>From</span>
      <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="erp-input" style={{ height: 24, fontSize: 10, width: 120 }} data-testid="sales-upd-from-date" />
      <span style={{ fontSize: 10, color: 'var(--erp-text-muted)' }}>To</span>
      <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="erp-input" style={{ height: 24, fontSize: 10, width: 120 }} data-testid="sales-upd-to-date" />
      <span style={{ fontSize: 10, color: 'var(--erp-border, #e2e8f0)', margin: '0 2px' }}>|</span>
      <Search size={10} style={{ color: 'var(--erp-text-muted)', flexShrink: 0 }} />
      <input placeholder="Customer…" value={partySearch} onChange={e => { setPartySearch(e.target.value); setPage(1); }} className="erp-input" style={{ height: 24, fontSize: 10, width: 130 }} data-testid="sales-upd-party-search" />
      {partySearch && <button onClick={() => setPartySearch('')} style={{ height: 24, padding: '0 4px', fontSize: 9, background: 'none', border: '1px solid var(--erp-border)', borderRadius: 3, cursor: 'pointer', color: '#94a3b8' }}><X size={9} /></button>}
      {editedCount > 0 && <span style={{ fontSize: 10, background: 'var(--erp-warning, #f59e0b)', color: '#fff', padding: '1px 6px', borderRadius: 10, fontWeight: 700 }}>{editedCount} unsaved</span>}
    </div>
  );

  return (
    <div className="erp-module" data-testid="sales-update-screen" data-dev-component="SalesUpdateScreen" data-dev-file="screens/finance/SalesUpdateScreen.jsx">
      <ERPAdvancedGrid
        title="Sales Update"
        columns={COLUMNS}
        data={pageData}
        total={displayRecords.length}
        idField="DocId"
        nameField="AccountName"
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={s => { setPageSize(s); setPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSort={(f) => { if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(f); setSortDir('asc'); } setPage(1); }}
        filters={filters}
        onFilterChange={f => { setFilters(f); setPage(1); }}
        loading={loading}
        onAddNew={null}
        testId="sales-update"
        exportFileName="SalesUpdate"
        headerActions={headerActions}
        cellRenderer={(field, value, row) => {
          if (field === 'DrAmount') return <span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 11 }}>{(parseFloat(value) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>;
          if (field === 'CommNarration') {
            const edits = editing[row.DocId] || {};
            const val = edits.CommNarration !== undefined ? edits.CommNarration : (value || '');
            return (
              <input
                value={val}
                onChange={e => setField(row.DocId, 'CommNarration', e.target.value)}
                className="erp-input"
                style={{ fontSize: 11, height: 24, width: '100%', minWidth: 120 }}
                placeholder="Narration..."
                data-testid={`sales-upd-narration-${row.DocId}`}
              />
            );
          }
          if (field === 'VoucherRef') {
            const edits = editing[row.DocId] || {};
            const val = edits.VoucherRef !== undefined ? edits.VoucherRef : (value || '');
            return (
              <input
                value={val}
                onChange={e => setField(row.DocId, 'VoucherRef', e.target.value)}
                className="erp-input"
                style={{ fontSize: 11, height: 24, width: 90 }}
                placeholder="Ref..."
                data-testid={`sales-upd-ref-${row.DocId}`}
              />
            );
          }
          if (field === '_actions') {
            const dirty = hasEdits(row.DocId);
            return (
              <button
                onClick={() => saveRow(row.DocId)}
                disabled={!dirty || saving === row.DocId}
                className={`erp-btn ${dirty ? 'erp-btn-primary' : 'erp-btn-ghost'}`}
                style={{ fontSize: 10, padding: '2px 8px', height: 24, display: 'flex', alignItems: 'center', gap: 4, opacity: dirty ? 1 : 0.4 }}
                data-testid={`sales-upd-save-${row.DocId}`}
              >
                {saving === row.DocId ? <RefreshCw size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={11} />}
                {saving === row.DocId ? '…' : 'Update'}
              </button>
            );
          }
          return null;
        }}
        cellClassName={(field) => field === 'DrAmount' ? 'text-right' : ''}
      />
    </div>
  );
}
